import { useEffect, useState } from 'react';
import {
  PaginationV2,
  SimpleTableBody,
  SimpleTableToolbar,
  SimpleTableToolbarButtons,
  SimpleTableContainer,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableHeaderButton,
  SimpleTableRow,
  SimpleTableData,
  SimpleTableWrapper,
  ROWS_PER_PAGE_OPTIONS,
} from 'cdk-radial';

import { SimpleTableHeaderWithSort } from './SimpleTableHeaderWithSort';
import { StyledBaseSimpleTable, StyledIllustrationContainer } from './StyledComponents';

export default function FeatureAccessGrid({
  rowData,
  isLoading,
  columns,
  toolbar,
}: any): JSX.Element {
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSizeObject, setPageSizeObject] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(9);

  const [totalRowCount, setTotalRowCount] = useState(rowData.length);
  const [sortDirection, setSortDirection] = useState();
  const [activeSortField, setActiveSortField] = useState();

  const handleSelect = (details:any) => {
    setPageSizeObject(details.pageSize);
    setCurrentPage(details.currentPage);
    setStartIndex(details.startIndex);
    setEndIndex(details.endIndex);
  };


  useEffect(() => {
    setTotalRowCount(rowData.length);
  }, [rowData]);

  useEffect(() =>{
    setStartIndex(0);
    setEndIndex(9);
  },[isLoading]);

  const sortData = (data: any, sortField: any, sortDirection = 'none') => {
    const sortedData = data.slice().sort((a: any, b: any) => {
      let comparison = 0;
      if (a[sortField] < b[sortField]) {
        comparison = -1;
      } else if (a[sortField] > b[sortField]) {
        comparison = 1;
      }
      return sortDirection === 'down' ? comparison * -1 : comparison;
    });
    return sortedData;
  };

  const sortHandler = (field: any) => {
    const sortDirections = ['none', 'up', 'down'];
    const newSortDirection =
      activeSortField !== field
        ? 'up'
        : sortDirections[
            (sortDirections.findIndex((item) => item === sortDirection) + 1) %
              sortDirections.length
          ];
    const newActiveSortField = newSortDirection === 'none' ? null : field;
    setActiveSortField(newActiveSortField);
    setSortDirection(newSortDirection as any);
    setCurrentPage(1);
  };

  const isActiveSort = (field: any) => activeSortField === field;

  const paginatedData = sortData(rowData, activeSortField, sortDirection).slice(
    startIndex,
    endIndex + 1
  );

  const tableHeader = (
    <SimpleTableHead>
      <SimpleTableRow>
        {columns.map((column: any, index: any) =>
          column.sortable ? (
            <SimpleTableHeaderWithSort
              id={`sort-${index}`}
              textAlign="left"
              colSpan="1"
              key={index}
              onClick={() => sortHandler(column.field)}
              sortActive={isActiveSort(column.field)}
              sortDirection={sortDirection}
            >
              {!column.headerRenderer && column.header}
              {column.headerRenderer && column.headerRenderer}
            </SimpleTableHeaderWithSort>
          ) : (
            <SimpleTableHeader textAlign="left" key={index}>
              {!column.headerRenderer && column.header}
              {column.headerRenderer && column.headerRenderer}
            </SimpleTableHeader>
          )
        )}
      </SimpleTableRow>
    </SimpleTableHead>
  );

  const loading = (
    <SimpleTableRow>
      <SimpleTableData colSpan={columns.length}>
        <StyledIllustrationContainer>Loading ...</StyledIllustrationContainer>
      </SimpleTableData>
    </SimpleTableRow>
  );

  const tableRows = paginatedData.map((row: any, index: any) => (
    <SimpleTableRow key={index}>
      {columns.map((column: any, colIndex: any) => (
        <SimpleTableData key={colIndex} textAlign="left" width="16%">
          {!column.columnRenderer && row[column.field]}
          {column.columnRenderer && column.columnRenderer(row, index)}
        </SimpleTableData>
      ))}
    </SimpleTableRow>
  ));

  return (
    <>
      <SimpleTableWrapper
        data-testid="simple-table-wrapper"
        style={{ marginTop: '20px' }}
      >
        {toolbar && <SimpleTableToolbar>
          <SimpleTableToolbarButtons>
            {toolbar?.map((tool: any, index: any) => (
              <SimpleTableHeaderButton key={index}> 
                {tool.component()}
              </SimpleTableHeaderButton>
            ))}
          </SimpleTableToolbarButtons>
        </SimpleTableToolbar> }

        <div style={{ marginTop: '20px' }}>
          <SimpleTableContainer>
            <StyledBaseSimpleTable aria-label="Table">
              {tableHeader}
              <SimpleTableBody>{tableRows}</SimpleTableBody>
              {isLoading && <SimpleTableBody>{loading}</SimpleTableBody>}
            </StyledBaseSimpleTable>
          </SimpleTableContainer>
        </div>
      </SimpleTableWrapper>

      <PaginationV2 
        ariaLabel="Pagination Controls" 
        currentPage={currentPage} 
        id="pagination-front-end-integration" 
        onSelect={handleSelect} 
        pageSize={pageSizeObject} 
        totalRowCount={totalRowCount} />
 
    </>
  );
}
